import {
  CTAButton,
  Disclosure,
  Heading1,
  Heading2,
  Li,
  Margin,
  P,
  SecondaryCTAButton,
  Strong,
  TextLink,
  Ul,
} from '@ovotech/nebula';
import { AnalyticsClick, useAnalytics } from '@ovotech/ui-tools';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom-v5';
import {
  LoadingStyledCard,
  StyledTermsCheckbox,
  StyledUl,
  ButtonGroupWrapper,
} from './DataSharingAndTCConsent.styled';
import { PageWithNavigation } from '@/src/components';
import { ButtonGroup } from '@/src/components/ButtonGroup/ButtonGroup';
import {
  CHARGE_ANYTIME_TERMS_AND_CONDITIONS_LINK,
  KALUZA_PRIVACY_POLICY_WEB_LINK,
  OVO_PRIVACY_POLICY_LINK,
  BETA_VEHICLE_TERMS_LINK,
  BETA_CHARGER_TERMS_LINK,
} from '@/src/constants/links';
import { SkeletonCard } from '@/src/components/SkeletonLoading/SkeletonCard';
import { TechnicalGlitch } from '@/src/pages/AnytimeEligibilityV2/components/TechnicalGlitch';
import {
  isServerError,
  getVolvoTrackingEventName,
  isStandardPlanViaCharger,
  useChargeAnytimeContext,
  EV,
  useChargeAnytimeSignupMutation,
  useEligibilityQuery,
  useEligiblePromotions,
  useChargeAnytimeMixpanelEventName,
  useChargeAnytimeProducts,
  PlanDetails,
  PLAN_IDS,
} from '@monovo/ev-anytime';
import { OptimizelyContext } from '@/src/utils/optimizely';
import { ROUTE_ANYTIME_ELIGIBILITY } from '@/src/constants/routes';
import { VolvoPromo } from '@/src/pages/AnytimeEligibilityV2/pages/DataSharingAndTCConsent/promos/VolvoPromo';
import { VolvoCheckbox } from '@/src/pages/AnytimeEligibilityV2/pages/DataSharingAndTCConsent/promos/VolvoCheckbox';
import { VolvoPartnerRateTerms } from '@/src/pages/AnytimeEligibilityV2/pages/DataSharingAndTCConsent/VolvoPartnerRateTerms';
import styled, { css } from 'styled-components';
import { theme } from '@ovotech/element-v4';

const StyledCTAButton = styled(CTAButton)`
  background-color: white;
  color: black;
  border-color: black;
`;

const PageHeader = styled(Heading1)`
  margin-top: 14px;
  font-size: 32px;
  color: white;
  ${({ theme: { mediaQueries } }) => css`
    ${mediaQueries.smallAndUp} {
      font-size: 48px;
    }
  `}
`;

const StyledBetaWarningCard = styled.div`
  flex-direction: row;
  padding-left: 10px;
  border-radius: 12px;
  padding-right: 10px;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: #ebefff;
`;

const StyledBetaWarningText = styled(P)`
  color: #1f3bcd;
  font-size: 16px;
  font-weight: 450;
  line-height: 22px;
  flex-shrink: 1;
  flex: 1;
`;

export const DataSharingAndTCConsent = () => {
  const { dispatch: analyticsDispatch } = useAnalytics();
  const { optimizely } = useContext(OptimizelyContext);
  const caDataConsentTitleTestEnabled = optimizely?.isFeatureEnabled(
    'ca_data_consent_title_test',
  );

  const [consentShareEVData, setConsentShareEVData] = useState(false);
  const [consentCATerms, setConsentCATerms] = useState(false);
  const [consentErrors, setConsentErrors] = useState(false);

  const history = useHistory();

  const {
    smartCharger,
    smartChargerModel,
    make,
    model,
    vehicleDeviceId,
    year,
    hasSolarPanels,
    hasInternetAccess,
    validPromoCode,
    selectedPlanId,
    integrationType,
    eligibleDevices,
    standardPlansViaCharger,
    eligibleProducts,
  } = useChargeAnytimeContext();

  const isBetaVehicleSelected = !!eligibleDevices?.vehicle?.isBeta;
  const isBetaChargerSelected = !!eligibleDevices?.charger?.isBeta;

  const betaWarningMessage = useMemo(() => {
    const devices = [];
    if (isBetaVehicleSelected) devices.push('vehicle');
    if (isBetaChargerSelected) devices.push('charger');

    const devicesText = devices.join(' and ');
    const verb = devices.length > 1 ? 'are' : 'is';
    const modelText = devices.length > 1 ? 'these models' : 'this model';

    return `Your ${devicesText} ${verb} currently in Beta testing. If you connect with a beta eligible device, you may experience difficulty connecting and charging while we continue to improve the integration with ${modelText}.`;
  }, [isBetaVehicleSelected, isBetaChargerSelected]);

  useEffect(() => {
    if (!smartCharger || !make) {
      history.replace(ROUTE_ANYTIME_ELIGIBILITY.SMART_CHARGER);
    }
    if (!selectedPlanId)
      history.replace(ROUTE_ANYTIME_ELIGIBILITY.PRODUCT_SELECTION);
  }, [smartCharger, make, selectedPlanId, history]);

  const { data: products, isFetching: isFetchingProducts } =
    useChargeAnytimeProducts('all');

  const { isFetching: isFetchingEligibility } = useEligibilityQuery();

  const chargeAnytimeSignupMutation = useChargeAnytimeSignupMutation();
  const { isError, error } = chargeAnytimeSignupMutation;

  const isChargeAnytimeGlitch = isError && isServerError(error);

  const { isEligibleVolvo } = useEligiblePromotions();

  const mixpanelEventName = useChargeAnytimeMixpanelEventName(integrationType);

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY.EVS_OVO_CHARGE_ANYTIME_CONSENT_VIEWED,
      properties: {
        DataConsentTitleTest: caDataConsentTitleTestEnabled,
      },
    });
    // `dispatch` method is not memoized, and it causes useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: 'EVS - EVS OVO Charge Anytime D2V Confirmation Eligibility Consent Viewed',
      properties: {
        DataConsentTitleTest: caDataConsentTitleTestEnabled,
      },
    });
    // `dispatch` method is not memoized, and it causes useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isChargeAnytimeGlitch) {
    return (
      <PageWithNavigation title="Charge Anytime">
        <TechnicalGlitch />
      </PageWithNavigation>
    );
  }

  if (
    chargeAnytimeSignupMutation.isLoading ||
    isFetchingEligibility ||
    isFetchingProducts
  ) {
    return (
      <PageWithNavigation title="Charge Anytime">
        <SkeletonCard />
      </PageWithNavigation>
    );
  }

  if (!smartCharger || !make || !integrationType || !selectedPlanId) {
    return (
      <PageWithNavigation title="Charge Anytime">
        <TechnicalGlitch />
      </PageWithNavigation>
    );
  }

  const changeConsentShareEVData = () => {
    setConsentShareEVData(!consentShareEVData);
  };

  const changeConsentCATerms = () => {
    setConsentCATerms(!consentCATerms);
  };

  // A Volvo customer offered OVO plans via the charger may pick one, in which
  // case the charger takes precedence: no vehicle in the signup, so no vehicle
  // promo either. They get the standard consent screen, not the Volvo one.
  // The separate Volvo-with-solar journey is unaffected — there the BAU plans
  // arrive in `eligibleProducts` and `standardPlansViaCharger` is null, so the
  // promo still applies.
  const isConnectingViaCharger = isStandardPlanViaCharger(
    selectedPlanId,
    standardPlansViaCharger,
    eligibleProducts,
  );
  const shouldShowVolvoPromo = isEligibleVolvo && !isConnectingViaCharger;

  // Which terms the customer agrees to. The free miles offer carries its own terms while
  // it runs; once it is withdrawn only the Volvo partner plan still has Volvo terms — a
  // Volvo owner on a BAU plan has nothing Volvo left to agree to.
  const termsVariant = shouldShowVolvoPromo
    ? 'volvoFreeMiles'
    : selectedPlanId === PLAN_IDS.PAYG_VOLVO
    ? 'volvoPartnerRate'
    : 'standard';

  const sendVolvoTracking = () => {
    const eventName = getVolvoTrackingEventName(
      shouldShowVolvoPromo,
      !!hasSolarPanels,
      smartCharger,
      validPromoCode,
    );
    if (eventName) {
      analyticsDispatch({
        type: 'view',
        name: eventName,
        properties: {},
      });
    }
  };

  const navigateToIntegration = () => {
    switch (integrationType) {
      case 'Volvo':
        history.push(ROUTE_ANYTIME_ELIGIBILITY.VOLVO_APP);
        break;
      case 'OvoCharge':
        history.push(ROUTE_ANYTIME_ELIGIBILITY.OVO_CHARGE_APP);
        break;
      case 'Hyundai':
        history.push(ROUTE_ANYTIME_ELIGIBILITY.HYUNDAI_APP);
        break;
      case 'Kia':
        history.push(ROUTE_ANYTIME_ELIGIBILITY.KIA_APP);
        break;
      default:
        history.push(ROUTE_ANYTIME_ELIGIBILITY.FALLOUT);
    }
  };

  const handleSuccess = () => {
    const selectedPlan = products?.find(
      (plan: PlanDetails) => plan.id === selectedPlanId,
    );

    const isVolvo =
      integrationType === 'Volvo' || selectedPlanId === 'payg-volvo';

    if (!isVolvo) {
      const eventTags: any = {
        $opt_event_properties: {
          plan_name: selectedPlan?.title || 'unknown_plan',
        },
      };
      optimizely?.track('charge_anytime_signup_complete', eventTags);
    }

    if (mixpanelEventName) {
      analyticsDispatch({
        type: 'view',
        name: EV.SIGNUP.EVS_OVO_CHARGE_ANYTIME_SIGN_UP_COMPLETE,
        properties: {
          name: mixpanelEventName,
          smartChargerSelection: smartCharger,
          smartChargerModelSelection: smartChargerModel,
          make: make,
          model: model,
        },
      });
    }
    setConsentErrors(false);
    navigateToIntegration();
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY.EVS_OVO_CHARGE_ANYTIME_ORDER_CREATED,
      properties: {},
    });
  };

  const handleError = (error: any) => {
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY.EVS_OVO_CHARGE_ANYTIME_ORDER_FAILED,
      properties: { error: error.message },
    });
  };

  const confirmConsent = async () => {
    optimizely?.track('charge_anytime_data_consent_confirm_clicked');
    if (consentShareEVData && consentCATerms) {
      sendVolvoTracking();
      analyticsDispatch({
        type: 'click',
        name: EV.SIGNUP.EVS_OVO_CHARGE_ANYTIME_CONSENT_CONFIRM_CLICKED,
        properties: {
          chargerName: smartCharger,
          chargerModel: smartChargerModel,
          carMake: make,
          carModel: model,
          carDeviceId: vehicleDeviceId,
          carYear: year,
          internetAccess: hasInternetAccess,
          solarPanels: hasSolarPanels,
          isEligibleVolvo: isEligibleVolvo,
          validPromoCode: validPromoCode,
          DataConsentTitleTest: caDataConsentTitleTestEnabled,
        },
      });
      analyticsDispatch({
        type: 'click',
        name: 'EVS - EVS OVO Drive Anytime consent yes clicked',
        properties: {
          chargerName: smartCharger,
          chargerModel: smartChargerModel,
          carMake: make,
          carModel: model,
          carDeviceId: vehicleDeviceId,
          carYear: year,
          internetAccess: hasInternetAccess,
          solarPanels: hasSolarPanels,
          isEligibleVolvo: isEligibleVolvo,
          validPromoCode: validPromoCode,
          DataConsentTitleTest: caDataConsentTitleTestEnabled,
        },
      });

      try {
        await chargeAnytimeSignupMutation.mutateAsync();
        handleSuccess();
      } catch (error: any) {
        handleError(error);
        return;
      }
    } else {
      setConsentShareEVData(consentShareEVData);
      setConsentCATerms(consentCATerms);
      setConsentErrors(true);
    }
  };

  return (
    <PageWithNavigation title="Charge Anytime">
      <PageHeader>
        {caDataConsentTitleTestEnabled
          ? 'Your privacy, our terms.'
          : 'Success! You can get Charge Anytime.'}
      </PageHeader>
      <div style={{ maxWidth: '880px' }}>
        <Margin top={5} />

        <LoadingStyledCard data-testid="anytime-consent-page">
          <P>You're nearly there, just confirm data sharing and legal stuff.</P>
          {shouldShowVolvoPromo && (
            <P>
              <Strong>Important:</Strong> To get the free miles offer you need
              to add the promo code.
            </P>
          )}
          <Margin top={4} bottom={4}>
            <Heading2
              style={{
                color: theme.core.color.brand.black,
              }}
            >
              Data sharing
            </Heading2>
          </Margin>
          <P>
            To bring you Charge Anytime, we need to share the following data
            with our trusted tech partner, Kaluza, and your EV charging partner:
          </P>
          <Ul>
            <Li>Your EV charger ID</Li>
            <Li>Your EV details</Li>
            <Li>Your EV energy use</Li>
          </Ul>
          <Margin top={4}>
            <StyledTermsCheckbox
              id="dataSharingEVData"
              label="I agree for my EV data to be shared with OVO, Kaluza and my EV charging partner. "
              checked={consentShareEVData}
              onChange={changeConsentShareEVData}
              error={
                !consentShareEVData &&
                consentErrors &&
                'Please tick ok to continue'
              }
              data-testid="dataSharingEVData"
            />
          </Margin>
          <Margin top={4}>
            <Disclosure name="more-about-data" title="More about data sharing">
              <P>
                We share your data with Kaluza and your charging partner (this
                is your EV charger manufacturer or your EV manufacturer). We do
                this to work out the most accurate charging costs for you.
              </P>
              <StyledUl>
                <Li>
                  <TextLink
                    href={KALUZA_PRIVACY_POLICY_WEB_LINK}
                    opensInNewWindow
                  >
                    See Kaluza Privacy Policy
                  </TextLink>
                </Li>
                <Li>
                  <TextLink href={OVO_PRIVACY_POLICY_LINK} opensInNewWindow>
                    See OVO Privacy Policy
                  </TextLink>
                </Li>
              </StyledUl>
            </Disclosure>
          </Margin>
          {shouldShowVolvoPromo && <VolvoPromo />}
          {(isBetaVehicleSelected || isBetaChargerSelected) && (
            <Margin top={4}>
              <StyledBetaWarningCard>
                <StyledBetaWarningText>
                  {betaWarningMessage}
                  {isBetaVehicleSelected && (
                    <>
                      {' '}
                      See our{' '}
                      <TextLink href={BETA_VEHICLE_TERMS_LINK} opensInNewWindow>
                        beta vehicle terms
                      </TextLink>
                      {isBetaChargerSelected ? ' and ' : '.'}
                    </>
                  )}
                  {isBetaChargerSelected && (
                    <>
                      {!isBetaVehicleSelected && ' See our '}
                      <TextLink href={BETA_CHARGER_TERMS_LINK} opensInNewWindow>
                        beta charger terms
                      </TextLink>
                      .
                    </>
                  )}
                </StyledBetaWarningText>
              </StyledBetaWarningCard>
            </Margin>
          )}
          <Heading2 style={{ color: theme.core.color.brand.black }}>
            Terms
          </Heading2>
          {termsVariant === 'standard' && (
            <>
              <P>
                To add Charge Anytime you'll need to agree to{' '}
                <TextLink
                  href={CHARGE_ANYTIME_TERMS_AND_CONDITIONS_LINK}
                  opensInNewWindow
                >
                  our terms
                </TextLink>
                .
              </P>
              <Margin top={4} bottom={8}>
                <StyledTermsCheckbox
                  id="chargeAnytimeTerms"
                  label="I agree to the Charge Anytime plan terms."
                  checked={consentCATerms}
                  onChange={changeConsentCATerms}
                  error={
                    !consentCATerms &&
                    consentErrors &&
                    'Please tick ok to continue'
                  }
                  data-testid="chargeAnytimeTerms"
                />
              </Margin>
            </>
          )}
          {termsVariant === 'volvoPartnerRate' && (
            <VolvoPartnerRateTerms
              checked={consentCATerms}
              setChecked={setConsentCATerms}
              isError={consentErrors}
            />
          )}
          {termsVariant === 'volvoFreeMiles' && (
            <VolvoCheckbox
              checked={consentCATerms}
              setChecked={setConsentCATerms}
              isError={consentErrors}
            />
          )}
        </LoadingStyledCard>
        <ButtonGroupWrapper>
          <ButtonGroup>
            <AnalyticsClick
              name={EV.SIGNUP.EVS_OVO_CHARGE_ANYTIME_CONSENT_BACK_CLICKED}
            >
              <StyledCTAButton
                onClick={history.goBack}
                data-testid="backButton"
              >
                Back
              </StyledCTAButton>
            </AnalyticsClick>
            <AnalyticsClick
              name={EV.SIGNUP.EVS_OVO_CHARGE_ANYTIME_CONSENT_CONFIRM_CLICKED}
            >
              <SecondaryCTAButton
                data-event-name={
                  'EVS - EVS OVO Drive Anytime consent yes clicked'
                }
                onClick={confirmConsent}
                data-testid="confirmConsentButton"
              >
                Agree to the terms
              </SecondaryCTAButton>
            </AnalyticsClick>
          </ButtonGroup>
        </ButtonGroupWrapper>
      </div>
    </PageWithNavigation>
  );
};
